import { zoneListQuerySchema } from '@chukua/common';
import { Select } from 'antd';
import { useCallback, useEffect } from 'react';
import { useRemoteSearchOptions, type SearchOption } from '../../../shared/hooks/use-remote-search-options';
import { listZones, type ZoneListItem } from '../zones.api';

const PAGE_SIZE = 20;

const toOption = (zone: ZoneListItem): SearchOption => ({ value: zone.code, label: zone.name });

/** The zone whose code is `code`, looked up by searching for the code. */
function findZoneByCode(code: string, kinds: string[] | undefined): Promise<SearchOption | undefined> {
  return listZones(zoneListQuerySchema.parse({ q: code, kind: kinds, pageSize: PAGE_SIZE })).then((page) => {
    const zone = page.items.find((candidate) => candidate.code === code);
    return zone && toOption(zone);
  });
}

/** Places only: a boundary is watched, never a destination (#447). The by-code lookup above stays unfiltered so a saved value still shows its name. */
function searchActiveZones(q: string | undefined, kinds: string[] | undefined): Promise<SearchOption[]> {
  return listZones(zoneListQuerySchema.parse({ q, kind: kinds, status: 'active', type: 'place', pageSize: PAGE_SIZE })).then((page) =>
    page.items.map(toOption),
  );
}

interface ZoneSearchSelectProps {
  id?: string;
  /** The zone's code: what a job's origin and destination store. */
  value?: string;
  onChange?: (value: string | undefined) => void;
  disabled?: boolean;
  /** Narrows the search to zones of these kinds — a container job's pickup (port/icd/cfs) or empty-return depot (depot). */
  kinds?: string[];
  /** Replaces the default prompt where two pickers sit side by side and need telling apart. */
  placeholder?: string;
  className?: string;
  'aria-label'?: string;
}

/**
 * Remote-search zone picker for a job's origin and destination: `GET /zones?q=`, active zones only, debounced —
 * a deployment's zones run past what one fetch should carry (CLAUDE.md → Lists and tables), so it searches, as
 * `CustomerSearchSelect` does. Opens on the first page of zones; a job reopened with a zone looks that one up by
 * its code, to show its name rather than the code.
 */
export function ZoneSearchSelect({
  id,
  value,
  onChange,
  disabled,
  kinds,
  placeholder = 'Select or search zones by name or code',
  className,
  'aria-label': ariaLabel,
}: ZoneSearchSelectProps) {
  const fetchZones = useCallback((q: string | undefined) => searchActiveZones(q, kinds), [kinds]);
  const { pin, selectProps } = useRemoteSearchOptions(fetchZones);

  useEffect(() => {
    if (!value) return;
    findZoneByCode(value, kinds).then((zone) => {
      if (zone) pin(zone);
    });
  }, [value, kinds, pin]);

  return (
    <Select
      {...selectProps}
      id={id}
      value={value}
      onChange={onChange}
      disabled={disabled}
      allowClear
      className={className}
      aria-label={ariaLabel}
      placeholder={placeholder}
      notFoundContent={selectProps.loading ? 'Searching…' : 'No zones found'}
    />
  );
}
